import React, { useEffect, useState, useRef } from 'react'
import type { GetServerSideProps } from 'next'

import SEO from '@/components/SEO'
import HeaderUnsigned from '@/components/LandingPage/Header'
import FormSignin from '@/components/Forms/Signin'
import FormRequestNewPassword from '@/components/Forms/RequestPassword'
import FormResetPassword from '@/components/Forms/ResetPassword'
import Login from '@/components/Login'

import Public from '@/helpers/PageTypes/Public'

import { useModal } from '@/contexts/ModalContext'

import { PAGE_PATHS } from '@/services/paths'
import { changeInputClass, validations } from '@/services/functions'
import {
  sendPostRequest,
  handleResponseStates,
  ENDPOINTS
} from '@/services/api'
import {
  invalidEmail,
  passwordTooShort,
  onTokenSent,
  onUserError,
  onTooManyRequests,
  onTooManyFailedAttempts,
  onUserNotFound,
  onLimitExceeded,
  onInvalidParameter,
  onUncaughtError
} from '@/services/userAlerts'

import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'

const baseURL = `${process.env.CDN_URL}/public/signin`
const imgCuriosity1 = `${baseURL}/curiosity1.webp`
const imgCuriosity2 = `${baseURL}/curiosity2.webp`

const SCREEN_IDS = {
  login: 'login',
  forgot_password: 'forgot',
  change_password: 'change'
}

const Signin = () => {
  const router = useRouter()
  const { t } = useTranslation()
  const { callModal, MODAL_TYPES } = useModal()
  const didMount = useRef(false)

  const [pageTitle, setPageTitle] = useState(' | Signin')
  const [currentScreen, setCurrentScreen] = useState(SCREEN_IDS.login)

  const [userName, setUserName] = useState('')
  const [password, setPassword] = useState('')
  const [rememberUser, setRememberUser] = useState(true)

  const [loading, setLoading] = useState(false)
  const [isAlertFromReset, setIsAlertFromReset] = useState(null)

  const [bannerTitle, setBannerTitle] = useState('')
  const [bannerCuriosity1, setBannerCuriosity1] = useState('')
  const [bannerCuriosity2, setBannerCuriosity2] = useState('')
  const [messagesConfirmAccount, setMessagesConfirmAccount] = useState('')
  const [messagesNotYetConfirmed1, setMessagesNotYetConfirmed1] = useState('')
  const [messagesNotYetConfirmed2, setMessagesNotYetConfirmed2] = useState('')
  const [messagesNotYetConfirmed3, setMessagesNotYetConfirmed3] = useState('')
  const [messagesNotYetConfirmed4, setMessagesNotYetConfirmed4] = useState('')

  let post = false
  let userNameValidation = false
  let passwordValidation = false

  useEffect(() => {
    setPageTitle(` | ${t('titles.login')}`)
    setBannerTitle(t('banner.title'))
    setBannerCuriosity1(t('banner.curiosity1'))
    setBannerCuriosity2(t('banner.curiosity2'))
    setMessagesConfirmAccount(t('messages.confirmAccount'))
    setMessagesNotYetConfirmed1(t('messages.notYetConfirmed1'))
    setMessagesNotYetConfirmed2(t('messages.notYetConfirmed2'))
    setMessagesNotYetConfirmed3(t('messages.notYetConfirmed3'))
    setMessagesNotYetConfirmed4(t('messages.notYetConfirmed4'))
  }, [t])

  useEffect(() => {
    changeInputClass('inp_email_login', userName, post, userNameValidation)
  }, [userName, post, userNameValidation])

  useEffect(() => {
    changeInputClass('inp_pass_login', password, post, passwordValidation)
  }, [password, post, passwordValidation])

  useEffect(() => {
    if (isAlertFromReset === null) return
    callModal(AlertSettings)
    setIsAlertFromReset(null)
  }, [isAlertFromReset])

  const validateSignInData = () => {
    post = true

    userNameValidation = validations.email(userName, 'inp_email_login', post)
    passwordValidation = validations.password(password, 'inp_pass_login', post)

    if (!userNameValidation) {
      invalidEmail()
      return false
    } else if (!passwordValidation) {
      passwordTooShort()
      return false
    }

    return true
  }

  const validateRequestPasswordData = () => {
    post = true

    userNameValidation = validations.email(
      userName,
      'inp_email_request_pass',
      post
    )

    if (!userNameValidation) {
      invalidEmail()
      return false
    }

    return true
  }

  const onSignIn = () => {
    setLoading(true)
    router.push(`${PAGE_PATHS.auth}?persist=${rememberUser}`)
  }

  const onRequestedPassword = response => {
    onTokenSent(response.msg.Destination)
    setCurrentScreen(SCREEN_IDS.change_password)
  }

  const responsesSignIn = [
    { name: 204, callback: onSignIn },
    { name: 'fail', callback: onUserError },
    { name: 'NotAuthorizedException', callback: onUserError },
    { name: 'InvalidPasswordException', callback: onUserError },
    { name: 'UserNotFoundException', callback: onUserNotFound },
    { name: 'TooManyRequestsException', callback: onTooManyRequests },
    {
      name: 'TooManyFailedAttemptsException',
      callback: onTooManyFailedAttempts
    },
    { name: 'LimitExceededException', callback: onLimitExceeded },
    { name: 'InvalidParameterException', callback: onInvalidParameter },
    { name: 'UserNotConfirmed', callback: () => setIsAlertFromReset(false) }
  ]

  const responsesRequestPassword = [
    {
      name: 200,
      callback: onRequestedPassword
    },
    {
      name: 201,
      callback: () => {
        setIsAlertFromReset(true)
        setCurrentScreen(SCREEN_IDS.login)
      }
    },
    { name: 'UserNotFoundException', callback: onUserNotFound },
    { name: 'LimitExceededException', callback: onLimitExceeded }
  ]

  const signUserIn = async (email, pass, isAuto) => {
    if (!isAuto && !validateSignInData()) return

    setLoading(true)

    await sendPostRequest(ENDPOINTS.sign_in, {
      username: email,
      password: pass
    })
      .then(response => {
        setLoading(false)
        handleResponseStates(response, responsesSignIn)
      })
      .catch(error => {
        const msg = error.data || error
        console.error('signUserIn error: ', msg)
        onUncaughtError()
      })
  }

  const requestNewPassword = async () => {
    if (!validateRequestPasswordData()) return

    setLoading(true)

    await sendPostRequest(ENDPOINTS.request_password, { username: userName })
      .then(response => {
        setLoading(false)
        handleResponseStates(response, responsesRequestPassword)
      })
      .catch(error => {
        const msg = error.data || error
        console.error('requestNewPassword error: ', msg)
        onUncaughtError()
      })
  }

  const renderCurrentScreen = () => {
    switch (currentScreen) {
      case SCREEN_IDS.login:
        return (
          <FormSignin
            userNameState={userName}
            userNameHandler={setUserName}
            passwordState={password}
            passwordHandler={setPassword}
            rememberState={rememberUser}
            rememberHandler={setRememberUser}
            loginHandler={signUserIn}
            screenHandler={setCurrentScreen}
            screenIds={SCREEN_IDS}
          />
        )

      case SCREEN_IDS.forgot_password:
        return (
          <FormRequestNewPassword
            userNameState={userName}
            userNameHandler={setUserName}
            requestTokenHandler={requestNewPassword}
            screenHandler={setCurrentScreen}
            screenIds={SCREEN_IDS}
          />
        )

      case SCREEN_IDS.change_password:
        return (
          <FormResetPassword
            userNameState={userName}
            passwordHandler={setPassword}
            loginHandler={signUserIn}
            requestTokenHandler={requestNewPassword}
            loadHandler={setLoading}
            screenHandler={setCurrentScreen}
            screenIds={SCREEN_IDS}
          />
        )

      default:
        return
    }
  }

  const SliderSettings = {
    dots: true,
    arrows: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1
  }

  const SlidesData = [
    {
      img: imgCuriosity1,
      title: bannerTitle,
      text: bannerCuriosity1
    },
    {
      img: imgCuriosity2,
      title: bannerTitle,
      text: bannerCuriosity2
    }
  ]

  const AlertSettings = {
    type: MODAL_TYPES.alert,
    config: {
      title: messagesConfirmAccount,
      text: (
        <>
          <p className="mb-1">
            {isAlertFromReset
              ? messagesNotYetConfirmed1
              : messagesNotYetConfirmed2}{' '}
            <b>{messagesNotYetConfirmed3}</b>
          </p>
          <p>{messagesNotYetConfirmed4}</p>
        </>
      ),
      img: {
        URL: `${process.env.CDN_URL}/public/alerts/email_person.webp`,
        width: 256,
        height: 219,
        alt: ''
      },
      circles: [
        { top: '35%', left: '6%', radius: 200, thickness: 40 },
        { top: '81%', left: '93%', radius: 120, thickness: 30 }
      ]
    }
  }

  return (
    <Public>
      <SEO title={pageTitle} />
      <div>
        <HeaderUnsigned pageName="signin" />
        <Login
          id="login-container"
          sliderSettings={SliderSettings}
          slidesData={SlidesData}
          controlsTop="60%"
          isLoading={loading}
          loadingClass="form-login"
          formBoxContent={renderCurrentScreen()}
        />
      </div>
    </Public>
  )
}

export default Signin

export const getServerSideProps: GetServerSideProps = async ctx => {
  const { req, res } = ctx

  const hasVetRefresh = Boolean(req.cookies['__Secure-RefreshTokenVet'])
  const hasVetId = Boolean(req.cookies['__Secure-IdTokenVet'])
  const shouldRevokeVet = hasVetRefresh || hasVetId

  if (shouldRevokeVet) {
    try {
      const cookieHeader = req.headers.cookie ?? ''

      await fetch(`${process.env.API_URL}/${ENDPOINTS.sign_out}`, {
        method: 'GET',
        headers: {
          'X-Application': 'vet',
          cookie: cookieHeader
        },
        credentials: 'include'
      })

      ctx.res.setHeader('Set-Cookie', [
        `__Secure-RefreshTokenVet=; Max-Age=0; Path=/; Secure; HttpOnly; SameSite=None`,
        `__Secure-IdTokenVet=; Max-Age=0; Path=/; Secure; HttpOnly; SameSite=None`
      ])
      console.debug('[Signin SSR]', 'cleaned vet cookies.')
    } catch (e) {
      console.debug('SSR Vet revoke skipped/failure:', e)
    }
  }

  return { props: {} }
}
